import ImageWrapper from "@/components/ImageWrapper";
import { MEAL_PLAN_TIME_ZONE_LOCALE, RecipeImage } from "@/utils/constant";
import {
  useAppDispatch,
  useAppSelector,
  useCallback,
  useEffect,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { addMealPlanAction } from "@/utils/redux/mealPlan/mealPlanSlice/actions";
import { setToastMessage } from "@/utils/redux/recipes/toastMessageSlice";
import { Radio } from "@dxp-feed/ui";
import { DatePicker } from "@/templates/mealPlan/_components/MealPlanDatepicker";
import { Box } from "@dxp-feed/ui/Box";
import { Button } from "@dxp-feed/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "@dxp-feed/ui/Dialog";
import { Text } from "@dxp-feed/ui/Text";
import { createMealPayload, decodeHtml } from "../common/commonRecipeFunctions";
import { formatMealPlanDate } from "@/helpers/useCommonFunctions";
import {
  fetchAndUpdateMealData,
  fetchDataFromAlgolia,
  fetchMealPlanDataFun,
  getMealCategoryName,
  mapToMealCategories,
  setAllMealPlanDataFunc,
} from "@/templates/mealPlan/commonFunctions/mealPlanUtils";
import { addRecipeToMealPlan } from "@/helpers/dataLayer";
import Link from "@/components/Link";
import { isDateOutOfMealPlanRange } from "@/templates/mealPlan/common";
import { useGigyaToken } from "../common/commonRecipeFunctions/getGigyaToken";
import Loader from "@/components/Loader";
import { mealPlanPageData, mealPlanPopupData } from "@/utils/aemDefaultData";
import useNetwork from "@/templates/recipes/common/commonRecipeFunctions/useNetwork";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { MEALS_LIMIT } from "@/utils/constant";
import useScrollBlocker from "../common/commonRecipeFunctions/useScrollBlocker";
import { MealPlanDataItem } from "@/utils/types/recipes";
interface RecipeCardMealPlanPopupProps {
  isRecipeCardMealPlanModalOpen: boolean;
  closeRecipeCardMealPlanModal: () => void;
  recipeData?: any;
  recipeSlug?: string;
}

const RecipeCardMealPlanPopup = ({
  isRecipeCardMealPlanModalOpen,
  closeRecipeCardMealPlanModal,
  recipeData,
  recipeSlug,
}: RecipeCardMealPlanPopupProps) => {
  const gigyaToken = useGigyaAccountInfo();
  const isOnline = useNetwork();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [scheduleDate, setScheduleDate] = useState<any>(
    formatMealPlanDate(new Date())
  );
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  const [savedMealPlans, setSavedMealPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { resetLoginParams } = useGigyaToken();
  const { bodyRef } = useScrollBlocker();
  const { uid: Uid } = useAppSelector((state) => state.gigyaslice);
  const { messages: MESSAGE } = useAppSelector(
    (state) => state.mealPlanMessageSlice
  );
  const {
    aemMealPlanData: aemMealPlanData,
    aemMealPlanPopupData: aemMealPlanPopupData,
  } = useAppSelector((state) => state.AemModalSlice);
  const mealPlanData: any = useAppSelector((state) => state.MealPlan);

  const rawMealCategories: any =
    aemMealPlanData?.mealCategories ??
    mealPlanPageData[0]?.mealCategories ??
    [];
  const mealCategories = mapToMealCategories(rawMealCategories);

  /**
   * Fetches meal plan data for the selected date range and Recipe.
   */
  const fetchData = useCallback(async () => {
    if (!isOnline) {
      dispatch(
        setToastMessage({
          message: `${MESSAGE.ERROR_MESSAGE.OFFLINE_ERROR}`,
          status: "other",
          color: "error",
        })
      );
      return;
    }
    const { idToken: token } = await gigyaToken();
    if (!token) {
      resetLoginParams();
      return;
    }
    if (!Uid || !scheduleDate || !recipeData?.objectID) return;
    try {
      setDataLoading(true);
      const data = (mealPlanData?.mealplanRecipeList ?? []).filter(
        (item: MealPlanDataItem) => item.recipe_id === recipeData?.objectID
      );
      setSavedMealPlans(data || []);
    } catch (err) {
      console.error("Error fetching meal plan data:", err);
      dispatch(
        setToastMessage({
          message: `${MESSAGE?.ERROR_MESSAGE?.SERVER_ERROR}`,
          status: "other",
          color: "error",
          messageOf: "meal",
        })
      );
    } finally {
      setDataLoading(false); // <-- always set loading false at the end (success or error)
    }
  }, [Uid, scheduleDate, recipeData?.objectID]);

  /**
   * useEffect hook to automatically fetch meal plan data when:
   * - The date range changes.
   * - The Recipe Id becomes available.
   */
  useEffect(() => {
    if (isRecipeCardMealPlanModalOpen) {
      void fetchData();
    }
  }, [scheduleDate, isRecipeCardMealPlanModalOpen, recipeData?.objectID]);

  /**
   *Saves selected recipes to a meal plan for the specified meal type and schedule date.
   */
  const saveMealPlan = async () => {
    if (!isOnline) {
      dispatch(
        setToastMessage({
          message: `${MESSAGE.ERROR_MESSAGE.OFFLINE_ERROR}`,
          status: "other",
          color: "error",
          messageOf: "meal",
        })
      );
      return;
    }
    const countForDate = (mealPlanData?.mealplanRecipeList ?? []).filter(
      (recipe: any) => {
        return formatMealPlanDate(recipe?.schedule_date) === scheduleDate;
      }
    ).length;

    if (countForDate >= MEALS_LIMIT) {
      dispatch(
        setToastMessage({
          message: `${MESSAGE.ERROR_MESSAGE.MEAL_PLAN_LIMIT_EXCEED}`,
          status: "mealError",
          messageOf: "meal",
          color: "error",
        })
      );
      return;
    }
    setLoading(true);
    try {
      const { idToken: token } = await gigyaToken();
      const mealTypeID = Number(selectedIndex) + 1;
      const payload = createMealPayload(
        mealTypeID,
        recipeData?.objectID,
        token,
        scheduleDate
      );

      const recipes = [{ recipe_id: recipeData?.objectID }];
      const data: any = await fetchDataFromAlgolia(recipes);

      const mealTypeName = getMealCategoryName(mealTypeID, rawMealCategories);

      data?.forEach((item: any) => {
        addRecipeToMealPlan({
          event: "add_recipe_to_meal_plan",
          recipe_name: decodeHtml(item?.title) || "",
          recipe_url:
            process.env.NEXT_PUBLIC_BASE_URL + "recipes/" + item?.pageSlug,
          recipe_meal_category: `${scheduleDate} (${mealTypeName})`,
          page_title: document.title,
        });
      });
      const result: any = await dispatch(addMealPlanAction(payload));
      if (result?.payload?.status == 401) {
        resetLoginParams();
        return;
      }
      if (result?.payload?.status == 200) {
        const data = result?.payload?.data?.MealPlan;
        await fetchAndUpdateMealData(
          data,
          dispatch,
          mealPlanData?.mealplanRecipeList
        );

        dispatch(
          setToastMessage({
            message: `${MESSAGE?.MEAL_PLAN_ADDED}`,
            status: "mealSuccess",
            color: "success",
            messageOf: "meal",
          })
        );
        closeModal();
        setSelectedIndex(null);
      } else if (result?.payload?.status == 409) {
        dispatch(
          setToastMessage({
            message: `${MESSAGE?.ERROR_MESSAGE?.MEAL_PLAN_ALREADY_EXIST}`,
            status: "mealError",
            color: "error",
            messageOf: "meal",
          })
        );
      } else {
        dispatch(
          setToastMessage({
            message: `${MESSAGE?.ERROR_MESSAGE?.MEAL_PLAN_NOT_ADDED}`,
            status: "mealError",
            color: "error",
            messageOf: "meal",
          })
        );
      }
    } catch (error) {
      console.error("Error saving meal plan:", error);
      dispatch(
        setToastMessage({
          message: `${MESSAGE?.ERROR_MESSAGE?.SERVER_ERROR}`,
          status: "other",
          color: "error",
          messageOf: "meal",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleDateChange = (selectedDate: {
    startDate: Date | null;
  }) => {
    if (!selectedDate?.startDate) {
      setIsSaveDisabled(true);
      return;
    }
    const selectedDateVal = selectedDate?.startDate?.toLocaleDateString(
      MEAL_PLAN_TIME_ZONE_LOCALE
    );
    setScheduleDate(formatMealPlanDate(selectedDateVal));
    setIsSaveDisabled(false);
  };
  const closeModal = () => {
    bodyRef?.current?.ownerDocument?.body?.classList.remove(
      "overflow-background"
    );

    if (bodyRef?.current?.ownerDocument?.body) {
      const body = bodyRef.current.ownerDocument.body;
      body.classList.remove("overflow-background");
      body.style.touchAction = "";
    }
    closeRecipeCardMealPlanModal();
    setDataLoading(true);
  };
  return (
    <Dialog
      isOpen={isRecipeCardMealPlanModalOpen}
      onClose={closeModal}
      positionClass="items-end md:items-center"
      className="max-w-full md:max-w-[708px] rounded-none md:rounded-lg  py-4 gap-4 flex flex-col"
    >
      <DialogHeader className="flex justify-between">
        <Box className="flex gap-2 flex-col" ref={bodyRef}>
          <Text as="h3" className="font-bold text-[20px]">
            {decodeHtml(recipeData?.title) ?? "Unknown Recipe"}
          </Text>
          <Box as="div" className="max-w-sm relative">
            <DatePicker
              disabledDates={isDateOutOfMealPlanRange}
              range={false}
              onDateChange={handleScheduleDateChange}
              defaultDate={new Date()}
              datePickerClassName="!static [&>button>span:last-child]:order-first  [&>button>div:last-child]:order-first [&>button]:justify-start min-w-[189px] [&>button]:w-[189px]  [&>button>span]:text-[14px] [&>button]:h-auto [&>button]:py-1 [&>button]:px-2 [&>button_.icon-close]:text-[6px] [&>button_.icon-month-calendar]:text-[18px] [&>button]:bg-grey150 [&>button]:border-none
              justify-start flex
              "
              className="z-[99999] w-[270px] right-0 !top-[34px]"
            />
          </Box>
        </Box>
        <Button
          className="text-grey950 font-bold text-lg"
          onClick={closeModal}
          aria-label={
            aemMealPlanPopupData?.closeModalAriaLabel ??
            mealPlanPopupData[0]?.closeModalAriaLabel
          }
          variant="icon"
          size="none"
        >
          <span className="demo-icon icon-close text-brandBlack text-[14px]"></span>
        </Button>
      </DialogHeader>
      {dataLoading ? (
        <Loader />
      ) : (
        <>
          <DialogBody className="py-0 flex flex-col gap-4">
            <Box className="relative bg-grey300 min-h-[150px] overflow-hidden pt-[57%] md:pt-[58%] xl:pt-[52%] rounded-[12px]">
              <ImageWrapper
                src={`${RecipeImage + recipeData?.featuredImage || recipeData?.featuredImage}`}
                alt={
                  recipeData?.title
                    ? `${recipeData?.title} ${
                        aemMealPlanPopupData?.deliciousRecipeAltText ??
                        mealPlanPopupData[0]?.deliciousRecipeAltText
                      }`
                    : (aemMealPlanPopupData?.recipeFromOurCollectionAltText ??
                      mealPlanPopupData[0]?.recipeFromOurCollectionAltText)
                }
                fill
                className="absolute top-0 left-0 object-cover"
              />
            </Box>
            <Box>
              <Text
                as="ul"
                className="flex flex-wrap justify-center gap-2 [&>li]:w-[125px] 2xs:[&>li]:w-[116px] xs:[&>li]:w-[125px]"
              >
                {mealCategories?.map((meal, index) => {
                  const isRadioDisabled = savedMealPlans?.some((item) => {
                    return (
                      formatMealPlanDate(item?.schedule_date) == scheduleDate &&
                      item?.meal_type_id == meal?.id &&
                      item?.recipe_id == recipeData?.objectID
                    );
                  });
                  return (
                    <Text
                      key={"meal-type-" + index}
                      as="li"
                      className={`border-[1px] border-solid border-neutral200 pr-4 rounded-[8px] 
                  ${
                    selectedIndex === index
                      ? "bg-brandSecondary text-white "
                      : "bg-transparent"
                  }
                  ${
                    isRadioDisabled
                      ? "!bg-grey300 opacity-50 text-black cursor-not-allowed "
                      : ""
                  }   
                  `}
                    >
                      <label
                        className={`flex items-center text-[14px] ${isRadioDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <Box
                          as="div"
                          className="w-12 h-12 flex items-center justify-center"
                        >
                          <Radio
                            name="sortOrder"
                            className={`h-[20px] w-[20px] after:w-[10px] after:h-[10px] before:border-neutral800 checked:before:border-neutral800 checked:after:bg-neutral800 ${selectedIndex === index ? "checked:before:border-white checked:after:bg-white " : ""}
                            ${isRadioDisabled ? "before:border-grey450" : ""} 
                            `}
                            checked={selectedIndex === index}
                            disabled={isRadioDisabled}
                            onChange={() => setSelectedIndex(index)}
                          />
                        </Box>
                        <Text as="span">{meal?.name ?? ""}</Text>
                      </label>
                    </Text>
                  );
                })}
              </Text>
            </Box>
          </DialogBody>

          <DialogFooter className="flex flex-col md:flex-row gap-2 justify-end ">
            <Button
              asChild
              variant="icon"
              className="gap-3 flex text-[14px] group"
            >
              {!recipeSlug && (
                <Link
                  href={
                    recipeData?.pageSlug
                      ? `/recipes/${recipeData?.pageSlug}`
                      : `/not-found`
                  }
                  aria-label={
                    aemMealPlanPopupData?.goToHomepageAriaLabel ??
                    mealPlanPopupData[0]?.goToHomepageAriaLabel
                  }
                  className="text-[14px] underline font-bold px-2 text-brandSecondary order-2 md:order-1"
                >
                  {aemMealPlanPopupData?.viewRecipeButton ??
                    mealPlanPopupData[0]?.viewRecipeButton}
                </Link>
              )}
            </Button>
            <Button
              variant="primary"
              className="h-[40px] text-[14px] font-normal order-1 md:order-2"
              onClick={saveMealPlan}
              disabled={
                selectedIndex == null ||
                isSaveDisabled ||
                loading ||
                dataLoading
              }
            >
              {loading
                ? (aemMealPlanPopupData?.savingButton ??
                  mealPlanPopupData[0]?.savingButton)
                : (aemMealPlanPopupData?.saveChangesButton ??
                  mealPlanPopupData[0]?.saveChangesButton)}
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
export default RecipeCardMealPlanPopup;
